import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { HeaderComponent } from '../../header/header.component';
import { Language } from '@ridango/core';
import { MenuItem } from '@ridango/core/lib/enums/features.enum';
import { filter, takeUntil } from 'rxjs/operators';
import { NavigationEnd } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { FooterAppMenuUtil } from './footer-app-menu.util';
import { FooterAppMenuAction } from './footer-app-menu.model';

const defaultMenuItems = [
    MenuItem.explore,
    MenuItem.schedule,
    MenuItem.ticket,
    MenuItem.card,
    MenuItem.disturbances,
    MenuItem.menu
];

@Component({
    selector: 'gl-footer-app',
    templateUrl: './footer-app.component.html',
    styleUrls: ['./footer-app.component.scss']
})
export class FooterAppComponent extends HeaderComponent implements OnInit {
    FooterAppMenuAction = FooterAppMenuAction;
    MenuItem = MenuItem;
    menuItems = defaultMenuItems;
    currentRoute: string;
    private readonly footerAppMenuUtil = new FooterAppMenuUtil();
    private routeBeforeMenu: string = null;
    @Output() languageChange = new EventEmitter();
    @Output() highContrastToggle: EventEmitter<void> = new EventEmitter();

    ngOnInit() {
        this.initSubscriptions();
    }

    handleLanguageChange(lang: Language) {
        this.languageChange.emit(lang);
    }

    toggleContrast(): void {
        this.highContrastToggle.emit();
    }

    trackByMenuItem(index: number, menuItem: MenuItem): MenuItem {
        return menuItem;
    }

    handleDefaultItemAction(action: FooterAppMenuAction): void {
        if (action === FooterAppMenuAction.HandleExploreClick) {
            void this.handleExploreClick();

            return;
        }

        this.closeMobileMenu();
    }

    toggleMobileMenu() {
        const wasOpen = this.isMenuOpen();
        const currentLang = this.translate.currentLang;
        const isMenuPageEnabled = !!this.env.MOBILE_MENU_CUSTOMIZATION?.IS_PAGE;

        this.applicationFacade.setMobileMenuOpen(!wasOpen);

        if (!wasOpen) {
            const openMenuState = this.footerAppMenuUtil.buildOpenMenuState(
                this.currentRoute,
                currentLang,
                isMenuPageEnabled
            );

            this.routeBeforeMenu = openMenuState.routeBeforeMenu;
            this.renderer.addClass(this.document.body, 'lock-scrolling');
            if (openMenuState.navigateCommands) {
                this.router.navigate(openMenuState.navigateCommands);
            }
        } else {
            const closeMenuNavigation = this.footerAppMenuUtil.buildCloseMenuNavigation(
                this.routeBeforeMenu,
                currentLang,
                isMenuPageEnabled
            );

            this.renderer.removeClass(this.document.body, 'lock-scrolling');

            if (closeMenuNavigation.navigateByUrl) {
                this.router.navigateByUrl(closeMenuNavigation.navigateByUrl);
            } else if (closeMenuNavigation.navigateCommands) {
                this.router.navigate(closeMenuNavigation.navigateCommands);
            }
        }
    }

    async handleExploreClick(): Promise<void> {
        if (this.env.FOOTER?.EXTERNAL_LINKS?.explore) {
            await Browser.open({ url: this.env.FOOTER.EXTERNAL_LINKS.explore });
        }
        this.closeMobileMenu();
    }

    private initSubscriptions(): void {
        this.networkStatus$.pipe(takeUntil(this.componentDestroyed$)).subscribe((status) => {
            if (!status) {
                this.menuItems = this.env.OFFLINE_MODE?.MENU_ITEMS as MenuItem[];
            } else {
                this.menuItems = this.env.FOOTER?.MENU_ITEMS || defaultMenuItems;

                if (!this.menuItems.includes(MenuItem.menu)) {
                    this.menuItems.push(MenuItem.menu);
                }
            }
        });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe((event: NavigationEnd) => {
                if (!event.url.includes('/mobile-menu')) {
                    this.routeBeforeMenu = event.url;
                }
                this.currentRoute = event.url;
            });
    }
}
